import clsx from 'clsx';
import Tooltip from '../_custom/Tooltip/Tooltip';
import BottomDrawer from '../_custom/BottomDrawer/BottomDrawer';
import CompanyLogoBadge from '../_custom/CompanyLogoBadge';
import SourceBadge from '../SourceBadge/SourceBadge';
import { SOURCE_BADGE_TONE } from '../SourceBadge/constants';
import InfoOutlineIcon from '../../assets/Svg/InfoOutlineIcon';
import { SOURCES_HEADING, SOURCES_HIDE_DELAY_MS, SOURCE_ROW_LOGO_SIZE } from './constants';
import { SourceRowsListProps, SourcesInfoLabelProps } from './types';
import { useSourcesInfoLabelLogic } from './useSourcesInfoLabelLogic';
import styles from './SourcesInfoLabel.module.scss';

const SourceRowsList = ({ rows }: SourceRowsListProps) => (
    <div className={styles.Rows}>
        {rows.map((row) => (
            <a
                key={row.key}
                className={clsx(styles.Row, { [styles.RowNoSub]: !row.subHeading })}
                href={row.url}
                target="_blank"
                rel="noopener noreferrer"
                title={row.heading}
            >
                <div className={styles.RowName}>
                    <span aria-hidden="true" className={styles.RowLogoFrame}>
                        <CompanyLogoBadge
                            websiteUrl={row.isAggregator ? undefined : row.url}
                            name={row.heading}
                            size={SOURCE_ROW_LOGO_SIZE}
                            shape="square"
                            className={styles.RowLogo}
                        />
                    </span>
                    <span className={styles.RowNameText}>{row.heading}</span>
                </div>
                {row.subHeading && <div className={styles.RowSub}>{row.subHeading}</div>}
            </a>
        ))}
    </div>
);

const SourcesInfoLabel = ({ value, sources }: SourcesInfoLabelProps) => {
    const { rows, isMobile, sheetOpen, openSheet, closeSheet, onTriggerKeyDown } = useSourcesInfoLabelLogic(sources);

    if (!rows.length) return <SourceBadge tone={SOURCE_BADGE_TONE.NEUTRAL} value={value} />;

    const badge = <SourceBadge tone={SOURCE_BADGE_TONE.NEUTRAL} value={value} trailing={<InfoOutlineIcon />} />;
    const triggerLabel = `View sources: ${value}`;

    if (isMobile) {
        return (
            <>
                <div
                    className={styles.MobileTrigger}
                    role="button"
                    tabIndex={0}
                    aria-haspopup="dialog"
                    aria-label={triggerLabel}
                    onClick={openSheet}
                    onKeyDown={onTriggerKeyDown}
                >
                    {badge}
                </div>
                {sheetOpen && (
                    <BottomDrawer onClose={closeSheet} height="auto" showHeading heading={SOURCES_HEADING} dialogLabel={SOURCES_HEADING}>
                        <SourceRowsList rows={rows} />
                    </BottomDrawer>
                )}
            </>
        );
    }

    return (
        <Tooltip
            content={
                <>
                    <div className={styles.Heading}>{SOURCES_HEADING}</div>
                    <SourceRowsList rows={rows} />
                </>
            }
            direction="bottom"
            focusable
            closeOnEscape
            ariaLabel={triggerLabel}
            hideDelay={SOURCES_HIDE_DELAY_MS}
            hideArrow
            small
            fitContent
            tipClassName={styles.SourcesTip}
            contentClassName={styles.SourcesContent}
        >
            {badge}
        </Tooltip>
    );
};

export default SourcesInfoLabel;
